import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../../components/common/Button';
import BasicInfoSection from './BasicInfoSection';
import FeedingSection from './FeedingSection';
import EnvironmentSection from './EnvironmentSection';
import SummarySection from './SummarySection';
import { useBabyProfiles } from '../../contexts/BabyProfileContext';
import PlanLimitExceededMessage from '../common/PlanLimitExceededMessage';
import { logger } from '../../utils/logger';
import { authService } from '../../services/authService';

// Styled Components
const FormContainer = styled.div`
  padding: 2rem 0;
`;

const FormCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(to right, var(--primary), var(--secondary));
  }
`;

const StepHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h2 {
    color: var(--primary);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--gray);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const StepIcon = styled.div`
  font-size: 1.5rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const ProgressIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 2.5rem 0 3rem;
  position: relative;
  padding: 0 10px;
  
  &::after {
    content: '';
    position: absolute;
    top: 24px;
    left: 40px;
    right: 40px;
    height: 3px;
    background-color: #e9ecef;
    z-index: 0;
  }
`;

const ProgressStep = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  z-index: 1;
  padding: 0 10px;
  
  .step-icon-container {
    position: relative;
    width: 50px;
    height: 50px;
    margin: 0 auto 0.75rem;
  }
  
  .step-number {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: ${props => props.active ? 'var(--primary)' : 'white'};
    color: ${props => props.active ? 'white' : 'var(--gray)'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
    position: relative;
    z-index: 2;
    transition: all 0.4s ease;
    border: 2px solid ${props => props.active ? 'var(--primary)' : '#e9ecef'};
    box-shadow: ${props => props.active ? '0 4px 10px rgba(233, 64, 87, 0.2)' : 'none'};
  }
  
  .step-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: ${props => props.active ? '1.2rem' : '1rem'};
    color: ${props => props.active ? 'white' : 'var(--gray)'};
    opacity: ${props => props.active ? 1 : 0.7};
    transition: all 0.3s ease;
  }
  
  .step-label {
    font-size: 0.95rem;
    color: ${props => props.active ? 'var(--primary)' : 'var(--gray)'};
    font-weight: ${props => props.active ? '600' : '500'};
    transition: all 0.3s ease;
    margin-top: 0.5rem;
  }
  
  .step-description {
    font-size: 0.8rem;
    color: ${props => props.active ? 'var(--dark)' : '#adb5bd'};
    margin-top: 0.25rem;
    transition: all 0.3s ease;
    display: ${props => props.active ? 'block' : 'none'};
    max-width: 120px;
    margin-left: auto;
    margin-right: auto;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 24px;
    right: -50%;
    width: 100%;
    height: 3px;
    background-color: ${props => props.completed ? 'var(--primary)' : 'transparent'};
    z-index: 0;
    transition: background-color 0.5s ease;
  }
  
  &:last-child::after {
    display: none;
  }
`;

const StepIndicator = styled.div`
  position: absolute;
  top: 24px;
  left: 40px;
  height: 3px;
  background-color: var(--primary);
  width: ${props => {
    if (props.currentStep === 1) return 'calc(0% - 40px)';
    if (props.currentStep === 2) return 'calc(50% - 40px)';
    if (props.currentStep === 3) return 'calc(100% - 40px)';
    return '0%';
  }};
  transition: width 0.6s ease;
  z-index: 0;
  box-shadow: 0 1px 3px rgba(233, 64, 87, 0.2);
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1rem;
    
    button {
      width: 100%;
    }
  }
`;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1rem;
    
    button {
      width: 100%;
    }
  }
`;

const GeneratePlanButton = styled(Button)`
  background-color: var(--secondary);
  border-color: var(--secondary);
  
  &:hover {
    background-color: var(--secondary-dark);
    border-color: var(--secondary-dark);
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const BabyProfileForm = ({ initialValues, onSubmitSuccess, isEditing = false }) => {
  const navigate = useNavigate();
  const { refreshProfiles, planLimitError, setPlanLimitError } = useBabyProfiles();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialValues || {
    // Step 1: Your Baby's Sleep Story
    babyName: '',
    gender: '',
    birthDate: '',
    isPremature: false,
    correctedAge: '',
    bedtime: '',
    wakeupTime: '',
    numberOfNaps: '',
    napDurations: '',
    sleepAssociations: {
      rocked: false,
      dummy: false,
      fedToSleep: false,
      independentlySleeps: false
    },
    selfSettles: '',
    
    // Step 2: Feeding and Night Waking Patterns
    feedingType: '',
    overnightFeeds: '',
    fallsAsleepDuringFeeds: '',
    wakesMultipleTimes: '',
    responseToWakes: {
      feed: false,
      rock: false,
      dummy: false,
      cuddle: false,
      wait: false,
      other: false
    },
    responseToWakesOther: '',
    
    // Step 3: Sleep Environment and Parenting Preferences
    sleepLocation: '',
    isDarkRoom: '',
    usesWhiteNoise: '',
    sleepwear: '',
    roomTemperature: '',
    roomSharing: '',
    parentingStylePreference: '',
    mainGoals: {
      longerStretches: false,
      selfSettling: false,
      betterNaps: false,
      reducingNightWakings: false
    },
    extraNotes: ''
  });
  
  // Form validation state
  const [errors, setErrors] = useState({});
  
  // Load saved form data from localStorage if available
  useEffect(() => {
    // Only load from localStorage if not in editing mode and no initialValues provided
    if (!isEditing && !initialValues) {
      // Removing localStorage loading to prevent form from being populated with old data
      // const savedData = localStorage.getItem('dozzy-baby-form');
      // if (savedData) {
      //   try {
      //     const parsedData = JSON.parse(savedData);
      //     setFormData(parsedData);
      //   } catch (error) {
      //     logger.error('Error parsing saved form data:', error);
      //   }
      // }
    } else if (initialValues) {
      // If initialValues are provided, use them to set the form data
      logger.debug('Setting form data from initialValues:', initialValues);
      setFormData(initialValues);
    }
  }, [isEditing, initialValues]);
  
  // For debugging
  useEffect(() => {
    if (formData) {
      logger.debug('Current form data:', {
        bedtime: formData.bedtime,
        wakeupTime: formData.wakeupTime,
        numberOfNaps: formData.numberOfNaps,
        napDurations: formData.napDurations
      });
    }
  }, [formData]);
  
  // Save form data to localStorage whenever it changes
  useEffect(() => {
    // Only save to localStorage if not in editing mode
    if (!isEditing) {
      localStorage.setItem('dozzy-baby-form', JSON.stringify(formData));
    }
  }, [formData, isEditing]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If editing, prevent changes to immutable fields
    if (isEditing && (name === 'babyName' || name === 'gender' || name === 'birthDate')) {
      return;
    }
    
    if (type === 'checkbox') {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: checked
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field when it's changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate form data for the current step
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Baby name is required for new profiles
      if (!isEditing && !formData.babyName) {
        newErrors.babyName = 'Baby\'s name is required';
      }
      
      // Gender is required for new profiles
      if (!isEditing && !formData.gender) {
        newErrors.gender = 'Gender is required';
      }
      
      // Birth date is required and must be for a child under 5 years old
      if (!isEditing && !formData.birthDate) {
        newErrors.birthDate = 'Birth date is required';
      } else if (formData.birthDate) {
        const birthDate = new Date(formData.birthDate);
        const today = new Date();
        const fiveYearsAgo = new Date();
        fiveYearsAgo.setFullYear(today.getFullYear() - 5);
        
        if (birthDate < fiveYearsAgo) {
          newErrors.birthDate = 'The sleeping program is for children up to 5 years old';
        }
      }
      
      // Corrected age is required if premature is checked
      if (formData.isPremature && !formData.correctedAge) {
        newErrors.correctedAge = 'Corrected age is required for premature babies';
      }
      
      // Bedtime is required
      if (!formData.bedtime) {
        newErrors.bedtime = 'Bedtime is required';
      }
      
      // Wake-up time is required
      if (!formData.wakeupTime) {
        newErrors.wakeupTime = 'Wake-up time is required';
      }
      
      // Number of naps is required
      if (!formData.numberOfNaps) {
        newErrors.numberOfNaps = 'Number of naps is required';
      }
      
      // Nap durations is required only if number of naps is not "0"
      if (formData.numberOfNaps !== "0" && !formData.napDurations) {
        newErrors.napDurations = 'Nap durations is required';
      }
      
      // Self-settles is required
      if (!formData.selfSettles) {
        newErrors.selfSettles = 'Please select if your baby self-settles';
      }
    } else if (step === 2) {
      // Feeding type is required
      if (!formData.feedingType) {
        newErrors.feedingType = 'Feeding type is required';
      }
      
      // Overnight feeds is required
      if (!formData.overnightFeeds) {
        newErrors.overnightFeeds = 'Number of overnight feeds is required';
      }
      
      // Falls asleep during feeds is required
      if (!formData.fallsAsleepDuringFeeds) {
        newErrors.fallsAsleepDuringFeeds = 'Please select if your baby falls asleep during feeds';
      }
      
      // Wakes multiple times is required
      if (!formData.wakesMultipleTimes) {
        newErrors.wakesMultipleTimes = 'Please select if your baby wakes multiple times overnight';
      }
      
      // If "other" is selected for response to wakes, the text field is required
      if (formData.responseToWakes.other && !formData.responseToWakesOther) {
        newErrors.responseToWakesOther = 'Please specify how you respond to wakes';
      }
      
      // At least one response to wakes should be selected
      const hasResponseSelected = Object.values(formData.responseToWakes).some(value => value === true);
      if (!hasResponseSelected) {
        newErrors.responseToWakes = 'Please select at least one response to wakes';
      }
    } else if (step === 3) {
      // Sleep location is required
      if (!formData.sleepLocation) {
        newErrors.sleepLocation = 'Sleep location is required';
      }
      
      // Dark room is required
      if (!formData.isDarkRoom) {
        newErrors.isDarkRoom = 'Please select if the baby\'s sleep space is dark';
      }
      
      // White noise is required
      if (!formData.usesWhiteNoise) {
        newErrors.usesWhiteNoise = 'Please select if white noise is used';
      }
      
      // Sleepwear is required
      if (!formData.sleepwear) {
        newErrors.sleepwear = 'Sleepwear is required';
      }
      
      // Room sharing is required
      if (!formData.roomSharing) {
        newErrors.roomSharing = 'Please select if your baby shares a room with a parent/caregiver';
      }
      
      // Parenting style preference is required
      if (!formData.parentingStylePreference) {
        newErrors.parentingStylePreference = 'Parenting style preference is required';
      }
      
      // At least one main goal should be selected
      const hasGoalSelected = Object.values(formData.mainGoals).some(value => value === true);
      if (!hasGoalSelected) {
        newErrors.mainGoals = 'Please select at least one main goal';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };
  
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };
  
  const goToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo(0, 0);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check token expiration before submission
    if (!authService.checkTokenExpiration()) {
      return; // Stop execution if token is expired
    }
    
    // Validate the final step before submission
    if (validateStep(currentStep)) {
      try {
        // Get the API URL from environment variables
        const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
        
        if (!apiUrl) {
          logger.error('Baby profiles API URL not found in environment variables');
          return;
        }
        
        // Get the authentication token from session storage
        const token = sessionStorage.getItem('idToken');
        
        if (!token) {
          logger.error('Authentication token not found');
          return;
        }
        
        // Create a clean, structured form data object with nested structure matching DynamoDB
        const modifiedFormData = {
          profileId: formData.profileId,
          babyName: formData.babyName,
          dateOfBirth: formData.birthDate,
          gender: formData.gender,
          sleepHabits: {
            bedtime: formData.bedtime,
            wakeupTime: formData.wakeupTime,
            numberOfNaps: formData.numberOfNaps,
            napDurations: formData.napDurations,
            selfSettles: formData.selfSettles,
            sleepAssociations: formData.sleepAssociations
          },
          feedingInfo: {
            feedingType: formData.feedingType,
            overnightFeeds: formData.overnightFeeds,
            fallsAsleepDuringFeeds: formData.fallsAsleepDuringFeeds,
            wakesMultipleTimes: formData.wakesMultipleTimes,
            responseToWakes: formData.responseToWakes,
            responseToWakesOther: formData.responseToWakesOther
          },
          sleepEnvironment: {
            sleepLocation: formData.sleepLocation,
            isDarkRoom: formData.isDarkRoom,
            usesWhiteNoise: formData.usesWhiteNoise,
            sleepwear: formData.sleepwear,
            roomTemperature: formData.roomTemperature,
            roomSharing: formData.roomSharing
          },
          healthInfo: {
            isPremature: formData.isPremature,
            correctedAge: formData.correctedAge,
            parentingStylePreference: formData.parentingStylePreference,
            mainGoals: formData.mainGoals,
            extraNotes: formData.extraNotes
          }
        };
        
        logger.debug(`${isEditing ? 'Updating' : 'Creating'} baby profile:`, modifiedFormData);
        
        // Determine if this is a create or update operation
        const method = isEditing ? 'PUT' : 'POST';
        const url = isEditing 
          ? `${apiUrl}/baby-profiles/${formData.profileId}` 
          : `${apiUrl}/baby-profiles`;
        
        // Make the API call to create or update the baby profile
        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(modifiedFormData)
        });
        
        if (!response.ok) {
          // Check if this is a plan limit error (403 status)
          if (response.status === 403) {
            const errorData = await response.json();
            setPlanLimitError(errorData);
            window.scrollTo(0, 0);
            return;
          }
          
          const errorText = await response.text();
          logger.error('API response error:', errorText);
          throw new Error(`API call failed with status: ${response.status}`);
        }
        
        const result = await response.json();
        logger.debug(`Baby profile ${isEditing ? 'updated' : 'created'}:`, result);
        
        // Clear localStorage after successful submission (only for create)
        if (!isEditing) {
          localStorage.removeItem('dozzy-baby-form');
        }
        
        // Refresh baby profiles before navigating
        try {
          await refreshProfiles();
          logger.debug('Baby profiles refreshed after operation');
        } catch (refreshError) {
          logger.error('Error refreshing profiles:', refreshError);
        }
        
        // Call the onSubmitSuccess callback if provided
        if (onSubmitSuccess) {
          onSubmitSuccess();
        } else {
          // Navigate to the sleep plan page instead of the dashboard
          const profileId = isEditing ? formData.profileId : result.profileId;
          navigate(`/view-sleep-plan/${profileId}`);
        }
      } catch (error) {
        logger.error(`Error ${isEditing ? 'updating' : 'creating'} baby profile:`, error);
        // You could add error handling here, such as displaying an error message to the user
      }
    }
  };
  
  const getStepTitle = () => {
    switch(currentStep) {
      case 1:
        return "Your Baby's Sleep Story";
      case 2:
        return "Feeding and Night Waking Patterns";
      case 3:
        return "Sleep Environment and Parenting Preferences";
      case 4:
        return "Review and Confirmation";
      default:
        return "Add Child";
    }
  };
  
  const getStepDescription = () => {
    switch(currentStep) {
      case 1:
        return "Let's get to know your little dreamer. Tell us about your baby's current sleep patterns.";
      case 2:
        return "Understanding your baby's feeding habits and how they wake at night helps us create a tailored plan.";
      case 3:
        return "Tell us about your baby's sleep environment and your parenting preferences.";
      case 4:
        return "Please review the information you've provided before we create your personalized sleep plan.";
      default:
        return "";
    }
  };
  
  const getStepIcon = () => {
    switch(currentStep) {
      case 1:
        return <i className="fas fa-moon"></i>;
      case 2:
        return <i className="fas fa-baby-carriage"></i>;
      case 3:
        return <i className="fas fa-home"></i>;
      case 4:
        return <i className="fas fa-clipboard-check"></i>;
      default:
        return null;
    }
  };
  
  const handleCloseError = () => {
    setPlanLimitError(null);
    navigate('/dashboard');
  };
  
  return (
    <FormContainer>
      {/* Show error message if plan limit is reached */}
      {planLimitError && (
        <PlanLimitExceededMessage 
          error={planLimitError} 
          onClose={handleCloseError} 
        />
      )}
      
      {!planLimitError && (
        <FormCard>
          <StepHeader>
            <StepIcon>{getStepIcon()}</StepIcon>
            <h2>{isEditing ? `Edit ${formData.babyName || 'Child'}'s Profile` : getStepTitle()}</h2>
            <p>{getStepDescription()}</p>
          </StepHeader>
          
          <ProgressIndicator>
            <StepIndicator currentStep={currentStep} />
            <ProgressStep active={currentStep >= 1} completed={currentStep > 1}>
              <div className="step-icon-container">
                <div className="step-number">
                  {currentStep > 1 ? <i className="fas fa-check"></i> : 1}
                </div>
                <div className="step-icon">
                  <i className="fas fa-moon"></i>
                </div>
              </div>
              <div className="step-label">Sleep Story</div>
              <div className="step-description">Your baby's sleep patterns</div>
            </ProgressStep>
            <ProgressStep active={currentStep >= 2} completed={currentStep > 2}>
              <div className="step-icon-container">
                <div className="step-number">
                  {currentStep > 2 ? <i className="fas fa-check"></i> : 2}
                </div>
                <div className="step-icon">
                  <i className="fas fa-baby-carriage"></i>
                </div>
              </div>
              <div className="step-label">Feeding & Waking</div>
              <div className="step-description">Night patterns and habits</div>
            </ProgressStep>
            <ProgressStep active={currentStep >= 3}>
              <div className="step-icon-container">
                <div className="step-number">
                  {currentStep > 3 ? <i className="fas fa-check"></i> : 3}
                </div>
                <div className="step-icon">
                  <i className="fas fa-home"></i>
                </div>
              </div>
              <div className="step-label">Environment</div>
              <div className="step-description">Sleep space and preferences</div>
            </ProgressStep>
          </ProgressIndicator>
          
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <BasicInfoSection 
                formData={formData} 
                handleChange={handleChange} 
                errors={errors}
                isEditing={isEditing}
              />
            )}
            
            {currentStep === 2 && (
              <FeedingSection 
                formData={formData} 
                handleChange={handleChange} 
                errors={errors} 
              />
            )}
            
            {currentStep === 3 && (
              <EnvironmentSection 
                formData={formData} 
                handleChange={handleChange} 
                errors={errors} 
              />
            )}
            
            {currentStep === 4 && (
              <SummarySection 
                formData={formData} 
                goToStep={goToStep} 
              />
            )}
            
            <FormActions>
              {currentStep === 4 ? (
                <ConfirmationActions className="d-flex justify-content-between w-100">
                  <div>
                    <Button type="button" variant="outline" onClick={prevStep}>
                      <i className="fas fa-arrow-left me-2"></i> Previous
                    </Button>
                  </div>
                  <div>
                    <GeneratePlanButton type="submit" variant="primary">
                      <i className="fas fa-magic"></i> {isEditing ? 'Update Profile' : 'Generate Sleep Plan'}
                    </GeneratePlanButton>
                  </div>
                </ConfirmationActions>
              ) : (
                <div className="d-flex justify-content-between w-100">
                  <div>
                    {currentStep > 1 && (
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <i className="fas fa-arrow-left me-2"></i> Previous
                      </Button>
                    )}
                  </div>
                  <div>
                    <Button type="button" variant="primary" onClick={nextStep}>
                      Next <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </div>
              )}
            </FormActions>
          </form>
        </FormCard>
      )}
    </FormContainer>
  );
};

export default BabyProfileForm;
